// dateFieldUtils.js

import { getLocalTimeZone, normalizeDateValue, timestampFromDate } from 'utilities/DateTime';
import {
  expandWeeklyOccurrences,
  normalizeRecurrence,
  toMillis as recurrenceToMillis,
} from 'utilities/DateTimeRangeRecurrence';

// Shared primitives for schema date/datetime inputs and modal save normalization.

export const RAW_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;
export const DEFAULT_MINUTE_STEP = 15;

// Returns true only for complete MM/DD/YYYY input.
export const isCompleteRawDate = (rawValue) => RAW_DATE_REGEX.test(rawValue || '');

// Returns a new Date shifted by `amount` days.
export const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const pad2 = (value) => String(value).padStart(2, '0');

export const generateTimeOptions = (minuteStep = DEFAULT_MINUTE_STEP) => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += minuteStep) {
      const value = `${pad2(hour)}:${pad2(minute)}`;
      const period = hour < 12 ? 'AM' : 'PM';
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      const label = `${hour12}:${pad2(minute)} ${period}`;
      options.push({ value, label });
    }
  }
  return options;
};

export const parseTimeKey = (timeKey) => {
  if (!timeKey || !/^\d{2}:\d{2}$/.test(timeKey)) return null;
  const [hourRaw, minuteRaw] = timeKey.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
};

// Adds hours to a HH:mm time key. dayOffset is 1 when the result crosses midnight.
export const addHoursToTimeKey = (timeKey, hours = 1) => {
  const parsed = parseTimeKey(timeKey);
  if (!parsed) return { timeKey: timeKey || '00:00', dayOffset: 0 };

  const totalMinutes = parsed.hour * 60 + parsed.minute + hours * 60;
  const dayOffset = Math.floor(totalMinutes / (24 * 60));
  const safeMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hour = Math.floor(safeMinutes / 60);
  const minute = safeMinutes % 60;

  return {
    timeKey: `${pad2(hour)}:${pad2(minute)}`,
    dayOffset,
  };
};

export const toTimeKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

export const getNearestSlotValue = (date, options, minuteStep = DEFAULT_MINUTE_STEP) => {
  const base = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const totalMinutes = base.getHours() * 60 + base.getMinutes();
  const rounded = Math.round(totalMinutes / minuteStep) * minuteStep;
  const safeMinutes = ((rounded % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hour = Math.floor(safeMinutes / 60);
  const minute = safeMinutes % 60;
  const candidate = `${pad2(hour)}:${pad2(minute)}`;
  if (options.some((opt) => opt.value === candidate)) return candidate;
  return options[0]?.value || '00:00';
};

export const applyTimeToDate = (date, timeKey) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const parsed = parseTimeKey(timeKey);
  if (!parsed) return new Date(date);
  const next = new Date(date);
  next.setHours(parsed.hour, parsed.minute, 0, 0);
  return next;
};

export const normalizeRangeForSave = (value, precision = 'date', options = {}) => {
  const startDate = normalizeDateValue(value?.startDate);
  if (!startDate) return null;

  const rawEndDate = normalizeDateValue(value?.endDate);
  const endDate = !rawEndDate || rawEndDate < startDate
    ? addDays(startDate, 1)
    : rawEndDate;

  const base = {
    startDate: timestampFromDate(startDate),
    startPrecision: precision,
    endDate: timestampFromDate(endDate),
    endPrecision: precision,
    isRange: true,
  };

  if (!Object.prototype.hasOwnProperty.call(value || {}, 'recurrence')) {
    return base;
  }

  if (value.recurrence == null) {
    return base;
  }

  const recurrenceResult = normalizeRecurrence(value.recurrence);
  if (!recurrenceResult.ok) {
    const error = new Error(recurrenceResult.error || 'Invalid recurrence.');
    error.code = 'invalid_recurrence';
    throw error;
  }

  if (!recurrenceResult.recurrence) {
    return base;
  }

  // Persist until as Timestamp when present for Firestore consistency.
  const persistedRecurrence = {
    ...recurrenceResult.recurrence,
    until: Number.isFinite(recurrenceResult.recurrence.until)
      ? timestampFromDate(new Date(recurrenceResult.recurrence.until))
      : null,
  };

  const timeZone = options.timeZone || getLocalTimeZone();
  const expansion = expandWeeklyOccurrences({
    startMs: recurrenceToMillis(base.startDate) ?? startDate.getTime(),
    endMs: recurrenceToMillis(base.endDate) ?? endDate.getTime(),
    recurrence: recurrenceResult.recurrence,
    timeZone,
  });

  if (!expansion.ok) {
    const error = new Error(expansion.error || 'Invalid recurrence series.');
    error.code = 'invalid_recurrence';
    throw error;
  }

  return {
    ...base,
    recurrence: persistedRecurrence,
  };
};